import type { Contact } from '@/types/contact';

/** The display name we'd auto-derive from a contact's own name/company fields. */
export function deriveDisplayName(contact?: Contact | null) {
  const type = contact?.contactType ?? 'RESIDENTIAL';
  return type === 'RESIDENTIAL'
    ? [contact?.firstName, contact?.lastName].filter(Boolean).join(' ').trim()
    : (contact?.companyName ?? '').trim();
}

/**
 * Commercial records use the company as their primary display name, but the
 * human contact must remain visible too. Return the person/title subline while
 * avoiding a duplicate when a deliberate display-name override is that person.
 */
export function deriveCommercialContactPersonLabel(
  contact?: Contact | null,
  primaryDisplayName?: string | null,
): string | null {
  if (!contact || contact.contactType === 'RESIDENTIAL') return null;

  const personName = [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim();
  const title = contact.title?.trim() ?? '';
  const normalizedPerson = personName.toLowerCase().replace(/\s+/g, ' ');
  const normalizedDisplay = primaryDisplayName?.trim().toLowerCase().replace(/\s+/g, ' ') ?? '';
  const visiblePersonName =
    normalizedPerson && normalizedPerson !== normalizedDisplay ? personName : '';
  const label = [visiblePersonName, title].filter(Boolean).join(' · ');
  return label || null;
}

/**
 * The display-name shapes our own code auto-generates for a contact — none of
 * which represent a human's deliberate choice:
 *  - the value derived from first+last / company,
 *  - the inbound-SMS / inbound-email placeholders `Unknown <phone|email>`,
 *  - the legacy inbound-email placeholder, which was the bare email address.
 * Matched by exact value (not an `Unknown…` prefix) so a real name like
 * "Unknown Ventures" is preserved while every actual placeholder is caught.
 */
function autoDisplayNames(contact?: Contact | null): Set<string> {
  const phone = (contact?.primaryPhone ?? '').trim();
  const email = (contact?.primaryEmail ?? '').trim();
  return new Set(
    [
      deriveDisplayName(contact),
      phone && `Unknown ${phone}`,
      email && `Unknown ${email}`,
      email, // legacy inbound-email placeholder stored the bare email
    ].filter((v): v is string => Boolean(v)),
  );
}

/**
 * True only when the stored display name was set deliberately — i.e. it isn't
 * blank and isn't one of the auto-generated shapes above. Auto display names are
 * seeded empty in edit forms so they keep re-deriving from first+last / company
 * (a manual override stays untouched).
 */
export function isManualDisplayName(contact?: Contact | null) {
  const dn = (contact?.displayName ?? '').trim();
  if (!dn) return false;
  return !autoDisplayNames(contact).has(dn);
}
